import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MRTToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
  MRT_ToggleFullScreenButton as MRTToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import FilterMultiSelectWithSearch, { exactMulti } from "./FilterMultiSelectWithSearch";

export default function ProjectPopup({
  open,
  onClose,
  bulkActionType,
  displayProjects = [],
  selectedProjectIds = new Set(),
  onSelectedProjectIdsChange,
  onPreview,
  onSubmit,
  submitting = false,
  title,
  previewOpen = false,
  onPreviewClose,
  selectedUserIds = [],
  companyUsers = [],
  allProjects = [],
}) {
  const actionTitle =
    title ?? (bulkActionType === "remove" ? "Remove users from projects" : "Add users to projects");

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Project ID",
        minSize: 80,
        size: 80,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        minSize: 220,
        size: 220,
        enableGlobalFilter: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        id: "project_type",
        header: "Type",
        minSize: 140,
        size: 140,
        accessorFn: (row) => row?.project_type?.name ?? null,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        id: "project_stage",
        header: "Stage",
        minSize: 140,
        size: 140,
        accessorFn: (row) => row?.project_stage?.name ?? null,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        id: "region",
        header: "Region",
        minSize: 160,
        size: 160,
        accessorFn: (row) => row?.region?.name ?? null,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        id: "departments",
        header: "Departments",
        minSize: 220,
        size: 220,
        accessorFn: (row) =>
          (row?.departments || [])
            .map((d) => d?.name)
            .filter(Boolean)
            .join(", ") || null,
        filterVariant: "multi-select",
        enableSorting: false,
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        id: "program",
        header: "Program",
        minSize: 160,
        size: 160,
        accessorFn: (row) => row?.program?.name ?? null,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
    ],
    []
  );

  // build MRT selection state from Set
  const rowSelection = useMemo(() => {
    const map = {};
    (selectedProjectIds ? Array.from(selectedProjectIds) : []).forEach(
      (id) => (map[String(id)] = true)
    );
    return map;
  }, [selectedProjectIds]);

  const table = useMaterialReactTable({
    /* ── Data & identity ─────────────────────────────────────────────── */
    columns,
    data: displayProjects ?? [],
    getRowId: (row) => String(row.id),

    /* ── Feature toggles ─────────────────────────────────────────────── */
    enableColumnActions: false,
    enableSorting: false,
    enablePagination: true,
    enableRowSelection: true,
    selectAllMode: "all",
    enableColumnResizing: true,
    layoutMode: "grid",

    /* ── Filtering & search ──────────────────────────────────────────── */
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableFilterMatchHighlighting: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    muiSearchTextFieldProps: {
      placeholder: "Search projects by name",
    },

    /* ── Initial table state ─────────────────────────────────────────── */
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: true, // open filters panel by default
      pagination: { pageSize: 10, pageIndex: 0 },
      columnPinning: { left: ["mrt-row-select", "name"] },
    },

    /* ── Controlled state & handlers ─────────────────────────────────── */
    state: { rowSelection },
    onRowSelectionChange: (updater) => {
      const next = typeof updater === "function" ? updater(rowSelection) : updater;
      const ids = new Set(
        Object.entries(next)
          .filter(([, v]) => Boolean(v))
          .map(([k]) => parseInt(k, 10))
      );
      onSelectedProjectIdsChange?.(ids);
    },

    /* ── Toolbar layout ──────────────────────────────────────────────── */
    positionGlobalFilter: "left",
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Filters cluster */}
        <Button
          variant="outlined"
          onClick={() => {
            table.resetColumnFilters();
            table.resetGlobalFilter();
          }}
        >
          Clear Filters
        </Button>
        <MRTToggleFiltersButton table={table} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* View controls */}
        <MRTShowHideColumnsButton table={table} />
        <MRTToggleDensePaddingButton table={table} />
        <MRTToggleFullScreenButton table={table} />
      </Box>
    ),

    /* ── Row UX ──────────────────────────────────────────────────────── */
    muiTableBodyRowProps: ({ row }) => ({
      // don’t toggle selection when clicking a control inside the row
      onClick: (e) => {
        if (e.target.closest("button, [role='button'], a, input, label")) return;
        row.getToggleSelectedHandler()(e);
      },
      sx: { cursor: "pointer" },
    }),

    /* ── Misc ───────────────────────────────────────────────────────── */
    positionToolbarAlertBanner: "none",
  });

  const selectionCount = selectedProjectIds?.size || 0;

  const selectedProjectsBulk = Array.from(selectedProjectIds || []);

  const isProjectApplicable = (project, userId) => {
    const isMember = Array.isArray(project.users)
      ? project.users.some((user) => user.id === userId)
      : false;
    return bulkActionType === "add" ? !isMember : isMember;
  };

  const getApplicableProjects = (userId) => {
    return selectedProjectsBulk
      .map((pid) => (allProjects || []).find((p) => p.id === pid))
      .filter(Boolean)
      .filter((p) => isProjectApplicable(p, userId));
  };

  const renderPreviewLines = () => {
    if (!selectedUserIds?.length || !selectedProjectsBulk.length)
      return <em>No users or projects selected.</em>;

    return selectedUserIds.map((uid) => {
      const user = (companyUsers || []).find((u) => u.id === uid);
      const applicableProjects = getApplicableProjects(uid);

      const content = applicableProjects.length
        ? applicableProjects.map((p) => p.name).join(", ")
        : bulkActionType === "add"
          ? "Already added to all selected projects"
          : "Not a part of any selected project";

      return (
        <Box key={uid} sx={{ mb: 1 }}>
          <strong>
            {user?.name || `User #${uid}`}{" "}
            {bulkActionType === "add" ? "added to: " : "removed from: "}
          </strong>
          {content}
        </Box>
      );
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span" sx={{ mr: 1 }}>
          {actionTitle}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box>
          <MaterialReactTable table={table} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ display: "flex", justifyContent: "space-between", px: 3, py: 1.5 }}>
        <Box>
          <Button
            onClick={onPreview}
            disabled={selectionCount === 0 || submitting}
            variant="outlined"
          >
            Preview
          </Button>
        </Box>

        <Box>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            variant="contained"
            disabled={selectionCount === 0 || submitting}
            sx={{ ml: 1 }}
            color={bulkActionType === "remove" ? "error" : "primary"}
          >
            {submitting ? <CircularProgress size={24} /> : bulkActionTitle(bulkActionType)}
          </Button>
        </Box>
      </DialogActions>

      {/* Preview dialog */}
      <Dialog open={Boolean(previewOpen)} onClose={onPreviewClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span" sx={{ mr: 1 }}>
            {bulkActionType === "add" ? "Preview: Bulk Add" : "Preview: Bulk Remove"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>{renderPreviewLines()}</DialogContent>
        <DialogActions>
          <Button onClick={onPreviewClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

function bulkActionTitle(type) {
  return type === "remove" ? "Remove from Selected Projects" : "Add to Selected Projects";
}
